import { useDl } from "../utils/dlToken";
import { useState, useEffect, useCallback } from "react";
import { previewDoc } from "../utils/previewDoc";
import axios from "axios";
import { Lock, ShieldCheck, RefreshCw, Trash2, LogOut, Mail, Building2, Globe, Inbox, FileCheck2, Receipt, Users, Reply, FileText, PenLine, Award, Paperclip, ClipboardList, Heart, ArrowLeft, Bell, BellRing, History, ArrowDownLeft, ArrowUpRight, Eye, EyeOff, ChevronDown, ChevronUp, Search, X, Signature, Forward, ShieldAlert } from "lucide-react";
import AdminApplications from "../components/AdminApplications";
import AdminInvoices from "../components/AdminInvoices";
import AdminAccounts from "../components/AdminAccounts";
import AdminTemplates from "../components/AdminTemplates";
import AdminSignPdf from "../components/AdminSignPdf";
import StaffSignature from "../components/StaffSignature";
import StaffTemplates from "../components/StaffTemplates";
import AdminRecognitions from "../components/AdminRecognitions";
import AdminWlePartners from "../components/AdminWlePartners";
import AdminHofBadges from "../components/AdminHofBadges";
import AdminRecords from "../components/AdminRecords";
import CharitativnaCinnost from "../components/CharitativnaCinnost";
import InstallAppButton from "../components/InstallAppButton";
import AdminBlacklist from "../components/AdminBlacklist";
import AdminRevocations from "../components/AdminRevocations";
import { enablePush, ensureSubscribed, syncBadge, sendPushTest, pushSupported } from "../utils/push";
import { EmailLangPicker, pickEmailLang } from "../components/EmailLangPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "saubereko_admin_token";
const ROLE_KEY = "saubereko_admin_role";
const PROFILE_KEY = "saubereko_staff_profile";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const dl = useDl(token);
  const [role, setRole] = useState(() => localStorage.getItem(ROLE_KEY) || "admin");
  const [profile, setProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || "null"); } catch { return null; }
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("applications");
  const [showPass, setShowPass] = useState(false);
  const [mktAgenda, setMktAgenda] = useState(null);
  const [counts, setCounts] = useState({ applications_new: 0, messages_new: 0, signed_new: 0 });
  const [reply, setReply] = useState(null);
  const [sending, setSending] = useState(false);
  const [archive, setArchive] = useState({});
  const [archiveOpen, setArchiveOpen] = useState({});
  const [archiveLoading, setArchiveLoading] = useState("");

  // Use a dedicated PWA manifest on the admin page so the installed icon opens the admin directly.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link ? link.getAttribute("href") : null;
    if (link) link.setAttribute("href", "/admin-manifest.json");
    const prevTitle = document.title;
    document.title = "Riadiace centrum — SauberEko®";

    // iOS: zabezpečí, že ikona pridaná na plochu z tejto stránky sa otvorí priamo tu.
    const metas = [];
    const setMeta = (name, content) => {
      let m = document.querySelector(`meta[name="${name}"]`);
      const created = !m;
      if (!m) { m = document.createElement("meta"); m.setAttribute("name", name); document.head.appendChild(m); }
      const prevContent = m.getAttribute("content");
      m.setAttribute("content", content);
      metas.push({ m, prevContent, created });
    };
    setMeta("apple-mobile-web-app-capable", "yes");
    setMeta("apple-mobile-web-app-status-bar-style", "black-translucent");
    setMeta("apple-mobile-web-app-title", "SauberEko Admin");

    // iOS home-screen icon (gold mark on black)
    let iconLink = document.querySelector('link[rel="apple-touch-icon"]');
    const iconCreated = !iconLink;
    const prevIcon = iconLink ? iconLink.getAttribute("href") : null;
    if (!iconLink) { iconLink = document.createElement("link"); iconLink.setAttribute("rel", "apple-touch-icon"); document.head.appendChild(iconLink); }
    iconLink.setAttribute("href", "/apple-touch-icon.png");

    return () => {
      if (link) link.setAttribute("href", "/manifest.json");
      document.title = prevTitle;
      metas.forEach(({ m, prevContent, created }) => {
        if (created) m.remove();
        else if (prevContent != null) m.setAttribute("content", prevContent);
      });
    };
  }, []);

  const fetchCounts = useCallback(async () => {
    if (!token || !["admin", "marketing"].includes(role)) return;
    try {
      const { data } = await axios.get(`${API}/admin/counts`, { headers: { Authorization: `Bearer ${token}` } });
      setCounts(data);
    } catch { /* ignore */ }
  }, [token, role]);

  useEffect(() => {
    if (!token || !["admin", "marketing"].includes(role)) return;
    fetchCounts();
    const id = setInterval(fetchCounts, 45000);
    return () => clearInterval(id);
  }, [fetchCounts, token, role]);

  const selectTab = async (name) => {
    setTab(name);
    try {
      if (name === "applications" && counts.applications_new > 0) {
        await axios.post(`${API}/admin/applications/mark-seen`, {}, { headers: { Authorization: `Bearer ${token}` } });
      } else if (name === "messages" && counts.messages_new > 0) {
        await axios.post(`${API}/admin/messages/mark-seen`, {}, { headers: { Authorization: `Bearer ${token}` } });
        syncBadge(0);
      }
      fetchCounts();
    } catch { /* ignore */ }
    if (name === "history") loadHistory();
  };

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      if (data.admin_2fa_required) {
        setChallenge(data.challenge);
        setPassword("");
        setLoading(false);
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      setError(err.response?.status === 429 ? "Príliš veľa pokusov. Skúste neskôr." : "Nesprávny e-mail alebo heslo.");
    } finally {
      setLoading(false);
    }
  };

  const verify2fa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/verify-2fa`, { challenge, pin: pin.trim() });
      localStorage.setItem(TOKEN_KEY, data.token);
      const r = data.role || "admin";
      localStorage.setItem(ROLE_KEY, r);
      setRole(r);
      if (data.profile) {
        localStorage.setItem(PROFILE_KEY, JSON.stringify(data.profile));
        setProfile(data.profile);
      }
      setToken(data.token);
      setChallenge("");
      setPin("");
    } catch (err) {
      setError(err.response?.status === 429 ? "Príliš veľa pokusov. Skúste neskôr." : "Nesprávny bezpečnostný kód.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(PROFILE_KEY);
    setToken("");
    setRole("admin");
    setProfile(null);
    setMessages([]);
  };

  const fetchMessages = useCallback(async () => {
    if (!token || !["admin", "marketing"].includes(role)) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(data);
    } catch (err) {
      if (err.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [token, role]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Web Push: silently (re)subscribe if already granted, and keep the icon badge in sync.
  useEffect(() => {
    if (!token || !["admin", "marketing"].includes(role)) return;
    ensureSubscribed(token);
  }, [token, role]);

  useEffect(() => {
    if (!token || !["admin", "marketing"].includes(role)) return;
    syncBadge(counts.messages_new || 0);
  }, [counts.messages_new, token, role]);

  const remove = async (id) => {
    if (!window.confirm("Naozaj zmazať túto žiadosť?")) return;
    try {
      await axios.delete(`${API}/admin/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages((m) => m.filter((x) => x.id !== id));
    } catch (err) {
      if (err.response?.status === 401) logout();
    }
  };

  const openReply = (m) => {
    setReply({ email: m.email, name: m.name || "", subject: `Re: Vaša správa pre SauberEko®`, body: "", attachments: [], lang: pickEmailLang(m.language) });
  };

  const openCompose = () => {
    setReply({ email: "", name: "", subject: "", body: "", attachments: [], compose: true, lang: "" });
  };

  const loadArchive = useCallback(async (email) => {
    setArchiveLoading(email);
    try {
      const { data } = await axios.get(`${API}/admin/correspondence`, {
        params: { email },
        headers: { Authorization: `Bearer ${token}` },
      });
      setArchive((s) => ({ ...s, [email]: data }));
    } catch {
      setArchive((s) => ({ ...s, [email]: [] }));
    } finally {
      setArchiveLoading("");
    }
  }, [token]);

  const [openMsg, setOpenMsg] = useState({});
  const [msgQuery, setMsgQuery] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [keepShown, setKeepShown] = useState({});
  const [previewing, setPreviewing] = useState(false);
  const unreadCount = messages.filter((m) => m.read === false).length;
  const norm = (x) => String(x || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const shownMessages = messages.filter((m) => {
    if (onlyUnread && m.read !== false && !keepShown[m.id]) return false;
    const q = norm(msgQuery.trim());
    return !q || [m.name, m.email, m.company, m.subject, m.message].some((f) => norm(f).includes(q));
  });
  const markRead = async (m, read) => {
    if (read) setKeepShown((k) => ({ ...k, [m.id]: true }));
    setMessages((list) => list.map((x) => (x.id === m.id ? { ...x, read } : x)));
    try {
      await axios.post(`${API}/admin/messages/${m.id}/read`, { read }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* ignore */ }
  };
  const previewReply = async () => {
    setPreviewing(true);
    try {
      const { data } = await axios.post(`${API}/admin/correspondence/preview`,
        { email: reply.email || "", subject: reply.subject || "", message: reply.body || "", attachments: (reply.attachments || []).map((a) => ({ filename: a.filename, content: "" })), language: reply.lang || null },
        { headers: { Authorization: `Bearer ${token}` } });
      setCorrView(data.html);
    } catch {
      alert("Náhľad sa nepodarilo zobraziť.");
    } finally { setPreviewing(false); }
  };
  const msgToolbar = () => messages.length > 0 && (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
      <div className="relative flex-1">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
        <input value={msgQuery} onChange={(e) => setMsgQuery(e.target.value)} data-testid="messages-search"
          placeholder="Hľadať podľa mena, e-mailu alebo textu…"
          className="w-full bg-[#0a0a0a] border border-white/15 pl-9 pr-9 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37] transition-colors" />
        {msgQuery && <button type="button" onClick={() => setMsgQuery("")} data-testid="messages-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"><X size={15} /></button>}
      </div>
      <button type="button" onClick={() => { setOnlyUnread((v) => !v); setKeepShown({}); }} data-testid="messages-unread-filter"
        className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm border transition-colors shrink-0 ${onlyUnread ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]" : "border-white/15 text-white/60 hover:text-white hover:border-[#D4AF37]/50"}`}>
        <span className="w-2 h-2 rounded-full bg-[#D4AF37]" /> Neprečítané ({unreadCount})
      </button>
    </div>
  );
  const msgEmpty = () => shownMessages.length === 0 && messages.length > 0 && (
    <p className="text-white/40 text-center py-12" data-testid="messages-no-results">Žiadna správa nezodpovedá hľadaniu.</p>
  );
  const msgCardCls = (m) => `border p-6 transition-colors ${m.read === false ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.04] hover:border-[#D4AF37]" : "border-white/10 bg-[#0a0a0a] hover:border-white/20"}`;
  const msgHead = (m) => (
    <>
      <p className="font-display text-xl inline-flex items-center gap-2.5">
        {m.read === false && <span className="w-2.5 h-2.5 rounded-full bg-[#D4AF37] shrink-0" data-testid="message-unread-dot" />}
        <span className={m.read === false ? "text-white font-semibold" : ""}>{m.name}</span>
        {m.read === false && <span className="text-[10px] uppercase tracking-wider bg-[#D4AF37] text-black px-1.5 py-0.5 font-sans font-semibold">Nové</span>}
      </p>
      {m.subject && <p className="text-white/60 text-sm mt-1" data-testid="message-subject">{m.subject}</p>}
    </>
  );
  const htmlToText = (html) => {
    const prepared = String(html || "").replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|tr|h\d|li)>/gi, "\n");
    const txt = new DOMParser().parseFromString(prepared, "text/html").body.textContent || "";
    return txt.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  };
  const forwardMail = ({ from, to, date, subject, text }) => setReply({
    email: "", compose: true, forward: true, lang: "", attachments: [],
    subject: /^(fwd?|fw):/i.test(subject || "") ? subject : `Fwd: ${subject || ""}`,
    body: `\n\n---------- Preposlaná správa ----------\nOd: ${from || "—"}\n${to ? `Komu: ${to}\n` : ""}Dátum: ${new Date(date).toLocaleString("sk-SK")}\nPredmet: ${subject || "—"}\n\n${text || ""}`,
  });
  const fwdBtn = (m) => (
    <button type="button" data-testid="message-forward"
      onClick={() => forwardMail({ from: `${m.name || ""} <${m.email}>`, date: m.created_at, subject: m.subject || "Správa z webu SauberEko®", text: m.message })}
      className="inline-flex items-center gap-2 border border-white/15 text-white/60 px-4 py-2 text-sm hover:border-[#D4AF37]/50 hover:text-white transition-colors">
      <Forward size={15} /> Preposlať
    </button>
  );
  const fwdHistory = (c) => forwardMail({
    from: c.direction === "out" ? "SauberEko®" : c.email, to: c.direction === "out" ? (c.to || []).join(", ") : "",
    date: c.created_at, subject: c.subject, text: c.body_text || htmlToText(c.body_html),
  });
  const readBtn = (m) => (
    <button type="button" onClick={() => markRead(m, m.read === false)} data-testid="message-read-toggle"
      className="inline-flex items-center gap-2 border border-white/15 text-white/50 px-4 py-2 text-sm hover:border-[#D4AF37]/50 hover:text-white transition-colors">
      {m.read === false ? <><Eye size={15} /> Označiť ako prečítané</> : <><EyeOff size={15} /> Označiť ako neprečítané</>}
    </button>
  );
  const toggleMsg = (id, e) => {
    const card = e?.currentTarget?.closest("[data-testid=admin-message-card]");
    if (openMsg[id] && card) setTimeout(() => card.scrollIntoView({ block: "start", behavior: "smooth" }), 30);
    setOpenMsg((s) => ({ ...s, [id]: !s[id] }));
    const m = messages.find((x) => x.id === id);
    if (m && m.read === false && !openMsg[id]) markRead(m, true);
  };
  const msgText = (m) => {
    const long = (m.message || "").length > 220 || (m.message || "").split("\n").length > 3;
    return (
    <div className="mt-4 border-t border-white/5 pt-4">
      <p onClick={(e) => { if (window.getSelection()?.toString()) return; if (long) toggleMsg(m.id, e); else if (m.read === false) markRead(m, true); }} data-testid="admin-message-text"
        className={`text-white/75 leading-relaxed whitespace-pre-wrap ${long ? "cursor-pointer" : ""} ${long && !openMsg[m.id] ? "line-clamp-3" : ""}`}>{m.message}</p>
      {long && <button type="button" onClick={(e) => toggleMsg(m.id, e)} data-testid="admin-message-toggle"
        className="mt-2 inline-flex items-center gap-1 text-[#D4AF37] text-xs hover:text-[#F2E3C6] transition-colors">
        {openMsg[m.id] ? <><ChevronUp size={14} /> Zbaliť</> : <><ChevronDown size={14} /> Rozbaliť celú správu</>}
      </button>}
    </div>
    );
  };

  const toggleArchive = (email) => {
    const willOpen = !archiveOpen[email];
    setArchiveOpen((s) => ({ ...s, [email]: willOpen }));
    if (willOpen && !archive[email]) loadArchive(email);
  };

  const [corrView, setCorrView] = useState(null);
  const viewCorrHtml = (html) => setCorrView(html || "<p>Bez obsahu</p>");

  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/correspondence/history`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(data);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [token]);

  const CORR_LABELS = {
    admin_message: "Odpoveď (Správy)",
    contact_reply: "Auto-odpoveď na dopyt",
    acceptance: "Oznámenie o zaradení",
    receipt: "Potvrdenie prijatia",
    document: "Odoslaný dokument",
    certificate_issued: "Certifikát vydaný",
  };

  const addAttachment = (att) =>
    setReply((r) => {
      if (!r) return r;
      if (r.attachments.some((a) => a.filename === att.filename)) return r;
      return { ...r, attachments: [...r.attachments, att] };
    });

  const removeAttachment = (filename) =>
    setReply((r) => (r ? { ...r, attachments: r.attachments.filter((a) => a.filename !== filename) } : r));

  const fileToB64 = (file) =>
    new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result).split(",")[1] || "");
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (f.size > 12 * 1024 * 1024) {
        alert(`Súbor ${f.name} je príliš veľký (max 12 MB).`);
        continue;
      }
      const content = await fileToB64(f);
      addAttachment({ filename: f.name, content, content_type: f.type });
    }
    e.target.value = "";
  };

  const attachTemplate = async (path, filename) => {
    try {
      const res = await fetch(path);
      const blob = await res.blob();
      const content = await fileToB64(new File([blob], filename, { type: "application/pdf" }));
      addAttachment({ filename, content, content_type: "application/pdf" });
    } catch {
      alert("Prílohu sa nepodarilo načítať.");
    }
  };

  const sendReply = async () => {
    if (reply?.compose && (!reply.email.trim() || !reply.email.includes("@"))) { alert("Zadajte platný e-mail príjemcu."); return; }
    if (!reply?.body.trim() || !reply?.subject.trim()) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/correspondence/send`,
        { email: reply.email, subject: reply.subject.trim(), message: reply.body.trim(), attachments: reply.attachments || [], language: reply.lang || null, outreach_brand: reply.outreach || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const sentEmail = reply.email;
      setReply(null);
      setArchiveOpen((s) => ({ ...s, [sentEmail]: true }));
      loadArchive(sentEmail);
    } catch (err) {
      alert(err.response?.status === 401 ? "Relácia vypršala." : "E-mail sa nepodarilo odoslať. Skúste znova.");
    } finally {
      setSending(false);
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("sk-SK", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  const openMktAgenda = async (name) => {
    setMktAgenda(name);
    try {
      if (name === "messages" && counts.messages_new > 0) {
        await axios.post(`${API}/admin/messages/mark-seen`, {}, { headers: { Authorization: `Bearer ${token}` } });
        syncBadge(0);
        fetchCounts();
      }
    } catch { /* ignore */ }
    if (name === "history") loadHistory();
  };

  const renderMessages = () => (
    <div>
      <div className="flex items-center justify-between gap-3 mb-5">
        <p className="text-white/40 text-sm">{messages.length === 0 ? "Zatiaľ žiadne správy." : `Správy (${messages.length})`}</p>
        <button type="button" onClick={openCompose} data-testid="compose-email-btn"
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-4 py-2.5 text-sm hover:bg-[#F2E3C6] transition-colors shrink-0">
          <Mail size={15} /> Napísať e-mail
        </button>
      </div>
      {msgToolbar()}
      {msgEmpty()}
      {messages.length === 0 ? (
        <p className="text-white/40 text-center py-16" data-testid="messages-empty">Zatiaľ žiadne správy. E-mail môžete napísať cez tlačidlo „Napísať e-mail" vyššie.</p>
      ) : (
      <div className="space-y-4">
        {shownMessages.map((m) => (
          <div key={m.id} data-testid="admin-message-card" className={msgCardCls(m)}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                {msgHead(m)}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-sm text-white/55">
                  <button type="button" onClick={() => { try { navigator.clipboard.writeText(m.email); } catch (e) {} }} title="Kliknutím skopírujete e-mail" className="inline-flex items-center gap-1.5 hover:text-[#D4AF37] cursor-pointer bg-transparent border-0 p-0 text-inherit">
                    <Mail size={14} /> {m.email}
                  </button>
                  {m.company && (<span className="inline-flex items-center gap-1.5"><Building2 size={14} /> {m.company}</span>)}
                  <span className="inline-flex items-center gap-1.5 uppercase"><Globe size={14} /> {m.language}</span>
                  {m.consent && <span className="text-[#D4AF37]">✓ súhlas GDPR</span>}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-white/35 text-xs">{fmtDate(m.created_at)}</span>
                <button onClick={() => remove(m.id)} data-testid="admin-delete-message" className="text-white/40 hover:text-red-400 transition-colors" aria-label="Delete"><Trash2 size={17} /></button>
              </div>
            </div>
            {msgText(m)}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button onClick={() => openReply(m)} data-testid="admin-reply-message" className="inline-flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-4 py-2 text-sm hover:bg-[#D4AF37]/10 transition-colors"><Reply size={15} /> Odpovedať</button>
              <button onClick={() => toggleArchive(m.email)} data-testid="admin-archive-toggle" className="inline-flex items-center gap-2 border border-white/15 text-white/60 px-4 py-2 text-sm hover:border-[#D4AF37]/50 hover:text-white transition-colors"><Inbox size={15} /> {archiveOpen[m.email] ? "Skryť archív" : "Archív odpovedí"}{archive[m.email]?.length ? ` (${archive[m.email].length})` : ""}</button>
              {fwdBtn(m)}
              {readBtn(m)}
            </div>
            {archiveOpen[m.email] && (
              <div className="mt-4 border border-white/10 bg-[#050505] p-4" data-testid="admin-archive-list">
                {archiveLoading === m.email && !archive[m.email] ? (
                  <p className="text-white/40 text-sm">Načítavam archív…</p>
                ) : (archive[m.email]?.length ? (
                  <div className="space-y-2">
                    {archive[m.email].map((c, ci) => (
                      <div key={ci} className="flex items-start justify-between gap-3 border-b border-white/5 pb-2 last:border-0 last:pb-0">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.direction === "out" ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "bg-white/10 text-white/60"}`}>{c.direction === "out" ? "Odoslané" : "Prijaté"}</span>
                            <span className="text-white/40 text-xs">{CORR_LABELS[c.ctype] || c.ctype}</span>
                            <span className="text-white/30 text-xs">· {fmtDate(c.created_at)}</span>
                          </div>
                          <p className="text-white/80 text-sm mt-1 truncate">{c.subject}</p>
                        </div>
                        {c.body_html && (<button onClick={() => viewCorrHtml(c.body_html)} data-testid="admin-archive-view" className="shrink-0 text-[#D4AF37] text-xs border border-[#D4AF37]/40 px-2.5 py-1 hover:bg-[#D4AF37]/10 transition-colors">Zobraziť</button>)}
                      </div>
                    ))}
                  </div>
                ) : (<p className="text-white/40 text-sm">Zatiaľ žiadna korešpondencia s týmto kontaktom.</p>))}
              </div>
            )}
          </div>
        ))}
      </div>
      )}
    </div>
  );

  const HISTORY_LABELS = { marketing_message: "Prijatá správa", admin_message: "Odoslaný e-mail", contact_reply: "Auto-odpoveď" };
  const renderHistory = () => (
    <div>
      <div className="flex items-center justify-between gap-3 mb-5">
        <p className="text-white/40 text-sm">{history == null ? "" : history.length === 0 ? "Zatiaľ žiadna komunikácia." : `Záznamy (${history.length})`}</p>
        <button type="button" onClick={loadHistory} data-testid="history-refresh"
          className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors shrink-0">
          <RefreshCw size={14} className={historyLoading ? "animate-spin" : ""} /> Obnoviť
        </button>
      </div>
      {historyLoading && history == null ? (
        <p className="text-white/40 text-center py-16">Načítavam…</p>
      ) : (history && history.length === 0) ? (
        <p className="text-white/40 text-center py-16" data-testid="history-empty">Zatiaľ žiadna komunikácia. E-maily, ktoré pošlete alebo prijmete cez web, sa uložia sem.</p>
      ) : (
        <div className="space-y-2" data-testid="history-list">
          {(history || []).map((c) => {
            const out = c.direction === "out";
            return (
              <div key={c.id} className="flex items-stretch gap-2">
              <button onClick={() => viewCorrHtml(c.body_html)} data-testid="history-item"
                className="min-w-0 flex-1 text-left flex items-start gap-3 border border-white/10 bg-[#0a0a0a] p-4 hover:border-[#D4AF37]/50 transition-colors">
                <span className={`mt-0.5 inline-flex items-center justify-center w-8 h-8 shrink-0 ${out ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "bg-white/10 text-white/70"}`}>
                  {out ? <ArrowUpRight size={16} /> : <ArrowDownLeft size={16} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${out ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-white/10 text-white/60"}`}>{out ? "Odoslané" : "Prijaté"}</span>
                    <span className="text-[10px] text-white/40">{HISTORY_LABELS[c.type] || c.type}</span>
                    <span className="text-sm text-white truncate">{c.subject || "(bez predmetu)"}</span>
                  </div>
                  <p className="text-white/40 text-xs mt-1 truncate">{out ? "Komu: " : "Od: "}{(c.to && c.to.length ? c.to.join(", ") : c.email) || "—"} · {new Date(c.created_at).toLocaleString("sk-SK")}</p>
                </div>
              </button>
              <button type="button" onClick={() => fwdHistory(c)} data-testid="history-forward" title="Preposlať"
                className="shrink-0 w-12 sm:w-auto sm:px-4 flex items-center justify-center gap-2 border border-white/10 bg-[#0a0a0a] text-white/50 text-sm hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors">
                <Forward size={16} /> <span className="hidden sm:inline">Preposlať</span>
              </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderModals = () => (
    <>
      {corrView !== null && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/80 sm:px-4" data-testid="corr-view-modal" onClick={() => setCorrView(null)}>
          <div className="w-full sm:max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[85vh] bg-[#0a0a0a] sm:border border-white/15 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <span className="text-white/70 text-sm">Náhľad korešpondencie</span>
              <button onClick={() => setCorrView(null)} data-testid="corr-view-close" className="w-10 h-10 -mr-2 flex items-center justify-center text-white/60 hover:text-white text-lg">✕</button>
            </div>
            <iframe title="Korešpondencia" sandbox="" srcDoc={previewDoc(corrView)} data-testid="corr-view-frame" className="w-full flex-1 min-h-0 sm:flex-none sm:h-[70vh] bg-white" style={{ border: 0 }} />
          </div>
        </div>
      )}
      {reply && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" data-testid="reply-modal" onClick={() => !sending && setReply(null)}>
          <div className="w-full max-w-lg border border-white/15 bg-[#0a0a0a] p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl mb-1">{reply.forward ? "Preposlať e-mail" : reply.compose ? "Napísať e-mail" : "Odpovedať"}</h3>
            {reply.compose ? (
              <div className="mb-5 mt-3">
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">Komu (e-mail príjemcu)</label>
                <input type="email" value={reply.email} onChange={(e) => setReply((r) => ({ ...r, email: e.target.value }))} data-testid="reply-to"
                  placeholder="email@prijemca.sk" className="w-full bg-[#050505] border border-white/15 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
              </div>
            ) : (
              <p className="text-white/45 text-sm mb-6 inline-flex items-center gap-2"><Mail size={14} /> {reply.email}</p>
            )}
            <div className="mb-5"><EmailLangPicker value={reply.lang || ""} onChange={(v) => setReply((r) => ({ ...r, lang: v }))} testid="reply-lang" /></div>
            <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">Predmet</label>
            <input value={reply.subject} onChange={(e) => setReply((r) => ({ ...r, subject: e.target.value }))} data-testid="reply-subject" className="w-full bg-[#050505] border border-white/15 px-3 py-2.5 text-white text-sm mb-5 focus:outline-none focus:border-[#D4AF37]" />
            <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">Správa</label>
            <textarea value={reply.body} onChange={(e) => setReply((r) => ({ ...r, body: e.target.value }))} data-testid="reply-body" rows={7} placeholder="Napíšte odpoveď…" className="w-full bg-[#050505] border border-white/15 px-3 py-2.5 text-white text-sm mb-4 focus:outline-none focus:border-[#D4AF37] resize-none" />
            <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">Prílohy</label>
            <div className="flex flex-wrap gap-2 mb-3">
              <label data-testid="reply-attach-file" className="inline-flex items-center gap-1.5 border border-white/20 text-white/70 px-3 py-1.5 text-xs hover:border-[#D4AF37]/50 hover:text-white cursor-pointer transition-colors"><Paperclip size={13} /> Nahrať súbor<input type="file" multiple onChange={onPickFiles} className="hidden" /></label>
            </div>
            {reply.attachments?.length > 0 && (
              <div className="mb-6 space-y-2" data-testid="reply-attachments-list">
                {reply.attachments.map((a) => (
                  <div key={a.filename} className="flex items-center justify-between gap-3 bg-[#050505] border border-white/10 px-3 py-2 text-sm">
                    <span className="inline-flex items-center gap-2 text-white/75 truncate"><Paperclip size={13} className="text-[#D4AF37] shrink-0" /> <span className="truncate">{a.filename}</span></span>
                    <button type="button" onClick={() => removeAttachment(a.filename)} data-testid={`reply-remove-${a.filename}`} className="text-white/40 hover:text-red-400 transition-colors shrink-0" aria-label="Odstrániť prílohu"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-3">
              <button onClick={() => setReply(null)} disabled={sending} className="px-5 py-2.5 text-sm text-white/60 hover:text-white transition-colors disabled:opacity-50">Zrušiť</button>
              <button type="button" onClick={previewReply} disabled={previewing || sending} data-testid="reply-preview" className="inline-flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-5 py-2.5 text-sm hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50"><Eye size={15} /> {previewing ? "Načítavam…" : "Náhľad"}</button>
              <button onClick={sendReply} disabled={sending || !reply.body.trim() || (reply.compose && !reply.email.trim())} data-testid="reply-send" className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-6 py-2.5 text-sm hover:bg-[#F2E3C6] transition-colors disabled:opacity-50">{sending ? "Odosielam…" : "Odoslať"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // ----- 2FA PIN gate -----
  if (!token && challenge) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white px-6" data-testid="admin-2fa-gate">
        <div className="grain" />
        <form
          onSubmit={verify2fa}
          data-testid="admin-2fa-form"
          className="relative w-full max-w-sm border border-white/10 bg-[#0a0a0a] p-8 md:p-10"
        >
          <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-b from-[#F2E3C6] to-[#D4AF37] text-black mb-6">
            <ShieldCheck size={22} />
          </div>
          <h1 className="font-display text-2xl mb-1">Bezpečnostné overenie</h1>
          <p className="text-white/40 text-sm mb-8">Zadajte bezpečnostný kód (PIN) pre vstup do Riadiaceho centra.</p>

          <label className="block text-xs tracking-[0.2em] uppercase text-white/40 mb-3">Bezpečnostný kód</label>
          <input
            type="password"
            inputMode="numeric"
            data-testid="admin-2fa-pin-input"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            autoFocus
            autoComplete="one-time-code"
            className="w-full bg-transparent border-b border-white/20 py-3 text-white text-center text-2xl tracking-[0.4em] focus:outline-none focus:border-[#D4AF37] transition-colors"
          />
          {error && <p data-testid="admin-2fa-error" className="text-red-400 text-sm mt-3">{error}</p>}

          <button
            type="submit"
            data-testid="admin-2fa-submit"
            disabled={loading || !pin.trim()}
            className="w-full mt-8 bg-[#D4AF37] text-black font-semibold py-3.5 hover:bg-[#F2E3C6] transition-colors disabled:opacity-60"
          >
            {loading ? "Overujem..." : "Overiť a vstúpiť"}
          </button>
          <button
            type="button"
            onClick={() => { setChallenge(""); setPin(""); setError(""); }}
            data-testid="admin-2fa-cancel"
            className="w-full mt-3 text-white/40 text-sm hover:text-white/70 transition-colors"
          >
            Späť na prihlásenie
          </button>
        </form>
      </div>
    );
  }

  // ----- Login gate -----
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white px-6">
        <div className="grain" />
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="relative w-full max-w-sm border border-white/10 bg-[#0a0a0a] p-8 md:p-10"
        >
          <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-b from-[#F2E3C6] to-[#D4AF37] text-black mb-6">
            <Lock size={22} />
          </div>
          <h1 className="font-display text-2xl mb-1">Riadiace centrum</h1>
          <p className="text-white/40 text-sm mb-8">SauberEko® — prístup pre oprávnených pracovníkov.</p>

          <label className="block text-xs tracking-[0.2em] uppercase text-white/40 mb-3">E-mail</label>
          <input
            type="email"
            data-testid="admin-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:outline-none focus:border-[#D4AF37] transition-colors mb-6"
          />

          <label className="block text-xs tracking-[0.2em] uppercase text-white/40 mb-3">Heslo</label>
          <div className="relative">
            <input
              type={showPass ? "text" : "password"}
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="w-full bg-transparent border-b border-white/20 py-3 pr-10 text-white focus:outline-none focus:border-[#D4AF37] transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowPass((s) => !s)}
              data-testid="admin-password-toggle"
              aria-label={showPass ? "Skryť heslo" : "Zobraziť heslo"}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#D4AF37] transition-colors p-1"
            >
              {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {error && <p data-testid="admin-login-error" className="text-red-400 text-sm mt-3">{error}</p>}

          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="w-full mt-8 bg-[#D4AF37] text-black font-semibold py-3.5 hover:bg-[#F2E3C6] transition-colors disabled:opacity-60"
          >
            {loading ? "Prihlasujem..." : "Vstúpiť"}
          </button>

          <div className="mt-7 pt-5 border-t border-white/10" data-testid="admin-install-hint">
            <InstallAppButton />
            <p className="text-white/40 text-xs leading-relaxed mt-3 text-center">
              Po inštalácii sa appka otvorí <strong className="text-white/60">rovno tu</strong>, v Riadiacom centre — bez návštevy webovej stránky.
              Ak vidíte starú ikonu, najprv ju zmažte.
            </p>
          </div>
        </form>
      </div>
    );
  }

  // ----- Marketing dashboard (Riaditeľka pre marketing a styk s verejnosťou) -----
  if (token && role === "marketing") {
    const agendas = [
      { key: "charity", label: "Charitatívna činnosť", icon: Heart, desc: "Šeky, zmluvy a vizitky" },
      { key: "recognitions", label: "Uznania", icon: Award, desc: "Uznania do Siene slávy (Tigik)" },
      { key: "messages", label: "Správy", icon: Inbox, desc: "Komunikácia e-mailom", badge: counts.messages_new },
      { key: "history", label: "História komunikácie", icon: History, desc: "Prijaté a odoslané e-maily" },
      { key: "sign", label: "Podpísať PDF", icon: FileCheck2, desc: "Elektronický podpis a pečiatka na dokument" },
      { key: "signature", label: "Môj podpis", icon: PenLine, desc: "Pridajte si vlastný podpis" },
      { key: "templates", label: "Šablóny", icon: FileText, desc: "Pečiatka, súhlas GDPR, NDA" },
    ];
    const current = agendas.find((a) => a.key === mktAgenda);
    return (
      <div className="h-screen flex flex-col bg-[#050505] text-white overflow-hidden" data-testid="marketing-dashboard">
        <div className="grain" />
        <header className="relative shrink-0 border-b border-white/10 bg-black/60 backdrop-blur-xl z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {mktAgenda ? (
                  <button onClick={() => setMktAgenda(null)} data-testid="marketing-back"
                    className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors shrink-0">
                    <ArrowLeft size={16} /> Späť
                  </button>
                ) : (
                  <Heart className="text-[#D4AF37] shrink-0" size={22} />
                )}
                <div className="min-w-0">
                  <p className="font-display text-lg sm:text-xl leading-none truncate">{current ? current.label : "Marketing a styk s verejnosťou"}</p>
                  <p className="text-white/40 text-xs mt-1 truncate">{profile?.name || "Timea Smrktová"}{profile?.title ? ` · ${profile.title}` : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <PushBell token={token} />
                <button onClick={logout} data-testid="marketing-logout"
                  className="inline-flex items-center gap-2 border border-white/15 px-3 sm:px-4 py-2 text-sm hover:border-red-400 hover:text-red-400 transition-colors shrink-0">
                  <LogOut size={15} /> <span className="hidden sm:inline">Odhlásiť</span>
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="relative flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
            {mktAgenda === null ? (
              <div data-testid="marketing-home">
                <h2 className="font-display text-2xl mb-6">Vaše agendy</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {agendas.map((a) => (
                    <button key={a.key} onClick={() => openMktAgenda(a.key)} data-testid={`marketing-agenda-${a.key}`}
                      className="relative text-left border border-white/10 bg-[#0a0a0a] p-6 hover:border-[#D4AF37]/60 transition-colors group">
                      <a.icon className="text-[#D4AF37] mb-4" size={28} />
                      <p className="font-display text-xl group-hover:text-[#D4AF37] transition-colors">{a.label}</p>
                      <p className="text-white/40 text-sm mt-1">{a.desc}</p>
                      {a.badge > 0 && <span className="absolute top-4 right-4"><NewBadge n={a.badge} testid={`marketing-badge-${a.key}`} /></span>}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <button onClick={() => setMktAgenda(null)} data-testid="marketing-back-top"
                  className="inline-flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-sm mb-6 transition-colors">
                  <ArrowLeft size={15} /> Späť na agendy
                </button>
                {mktAgenda === "charity" ? (
                  <CharitativnaCinnost token={token} role="marketing" />
                ) : mktAgenda === "recognitions" ? (
                  <AdminRecognitions token={token} />
                ) : mktAgenda === "sign" ? (
                  <AdminSignPdf token={token}
                    uploadUrl={`${API}/staff/sign-pdf/upload`}
                    applyUrl={`${API}/staff/sign-pdf/apply`}
                    assetUrl={(type) => `${API}/staff/asset/${type === "stamp" ? "stamp" : "signature"}?token=${dl}`} />
                ) : mktAgenda === "signature" ? (
                  <StaffSignature token={token} />
                ) : mktAgenda === "templates" ? (
                  <StaffTemplates token={token} />
                ) : mktAgenda === "history" ? (
                  renderHistory()
                ) : (
                  renderMessages()
                )}
              </>
            )}
          </div>
        </main>
        {renderModals()}
      </div>
    );
  }

  // ----- Dashboard -----
  return (
    <div className="h-screen flex flex-col bg-[#050505] text-white overflow-hidden" data-testid="admin-dashboard">
      <div className="grain" />
      <header className="relative shrink-0 border-b border-white/10 bg-black/60 backdrop-blur-xl z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <ShieldCheck className="text-[#D4AF37] shrink-0" size={22} />
              <div className="min-w-0">
                <p className="font-display text-lg sm:text-xl leading-none truncate">Riadiace centrum</p>
                <p className="text-white/40 text-xs mt-1">SauberEko®</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <PushBell token={token} />
              <button
                onClick={async () => {
                  if (!window.confirm("Odhlásiť všetky zariadenia (vás aj marketing)? Všetci sa budú musieť znova prihlásiť.")) return;
                  try { await axios.post(`${API}/admin/logout-all`, {}, { headers: { Authorization: `Bearer ${token}` } }); } catch { /* token už neplatí */ }
                  logout();
                }}
                data-testid="admin-logout-all" title="Odhlásiť všetky zariadenia"
                className="inline-flex items-center gap-2 border border-red-400/40 px-3 py-2 text-xs text-red-200 hover:border-red-400 hover:text-red-400 transition-colors shrink-0"
              >
                Odhlásiť všade
              </button>
              <button
                onClick={logout}
                data-testid="admin-logout"
                className="inline-flex items-center gap-2 border border-white/15 px-3 sm:px-4 py-2 text-sm hover:border-red-400 hover:text-red-400 transition-colors shrink-0"
              >
                <LogOut size={15} /> <span className="hidden sm:inline">Odhlásiť</span>
              </button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:inline-flex border border-white/10 p-1 gap-1 sm:gap-0 w-full sm:w-auto">
            <button onClick={() => selectTab("applications")} data-testid="tab-applications"
              className={`relative inline-flex items-center justify-center gap-2 px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${tab === "applications" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              <FileCheck2 size={15} /> Žiadosti
              <NewBadge n={counts.applications_new + (counts.signed_new || 0)} testid="badge-applications" />
            </button>
            <button onClick={() => selectTab("mentorsign")} data-testid="tab-mentorsign"
              className={`relative inline-flex items-center justify-center gap-2 px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${tab === "mentorsign" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              <Signature size={15} /> Na podpis
              <NewBadge n={counts.mentor_pending || 0} testid="badge-mentorsign" />
            </button>
            <button onClick={() => selectTab("invoices")} data-testid="tab-invoices"
              className={`relative inline-flex items-center justify-center gap-2 px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${tab === "invoices" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              <Receipt size={15} /> Faktúry
            </button>
            <button onClick={() => selectTab("messages")} data-testid="tab-messages"
              className={`relative inline-flex items-center justify-center gap-2 px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${tab === "messages" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              <Inbox size={15} /> Správy
              <NewBadge n={counts.messages_new} testid="badge-messages" />
            </button>
            <button onClick={() => selectTab("accounts")} data-testid="tab-accounts"
              className={`relative inline-flex items-center justify-center gap-2 px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${tab === "accounts" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              <Users size={15} /> Účty
            </button>
            <button onClick={() => selectTab("templates")} data-testid="tab-templates"
              className={`relative inline-flex items-center justify-center gap-2 px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${tab === "templates" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              <FileText size={15} /> Šablóny
            </button>
            <button onClick={() => selectTab("signpdf")} data-testid="tab-signpdf"
              className={`relative inline-flex items-center justify-center gap-2 px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${tab === "signpdf" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              <PenLine size={15} /> Podpísať PDF
            </button>
            <button onClick={() => selectTab("recognitions")} data-testid="tab-recognitions"
              className={`relative inline-flex items-center justify-center gap-2 px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${tab === "recognitions" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              <Award size={15} /> Uznania
            </button>
            <button onClick={() => selectTab("records")} data-testid="tab-records"
              className={`relative inline-flex items-center justify-center gap-2 px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${tab === "records" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              <ClipboardList size={15} /> Evidencia
            </button>
            <button onClick={() => selectTab("charity")} data-testid="tab-charity"
              className={`relative inline-flex items-center justify-center gap-2 px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${tab === "charity" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              <Heart size={15} /> Charita
            </button>
            <button onClick={() => selectTab("blacklist")} data-testid="tab-blacklist"
              className={`relative inline-flex items-center justify-center gap-2 px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${tab === "blacklist" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              <ShieldAlert size={15} /> Čierna listina
            </button>
            <button onClick={() => selectTab("history")} data-testid="tab-history"
              className={`relative inline-flex items-center justify-center gap-2 px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap transition-colors ${tab === "history" ? "bg-[#D4AF37] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              <History size={15} /> História
            </button>
          </div>
        </div>
      </header>

      <main className="relative flex-1 overflow-y-auto" data-testid="admin-scroll">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {tab === "applications" ? (
          <AdminApplications token={token} />
        ) : tab === "invoices" ? (
          <AdminInvoices token={token} />
        ) : tab === "accounts" ? (
          <AdminAccounts token={token} onOpenMentorSign={() => selectTab("mentorsign")} />
        ) : tab === "templates" ? (
          <AdminTemplates token={token} onCompose={(d) => setReply({ email: "", compose: true, attachments: [], ...d })} />
        ) : tab === "signpdf" ? (
          <AdminSignPdf token={token} />
        ) : tab === "recognitions" ? (
          <AdminRecognitions token={token} />
        ) : tab === "records" ? (
          <>
            <AdminRecords token={token} onChange={fetchCounts} />
            <AdminWlePartners token={token} />
            <AdminHofBadges token={token} />
          </>
        ) : tab === "mentorsign" ? (
          <AdminRecords token={token} pendingOnly onChange={fetchCounts} />
        ) : tab === "charity" ? (
          <CharitativnaCinnost token={token} role="admin" />
        ) : tab === "blacklist" ? (
          <>
            <AdminBlacklist token={token} />
            <AdminRevocations token={token} />
          </>
        ) : tab === "history" ? (
          renderHistory()
        ) : (
          <div>
            <div className="flex items-center justify-between gap-3 mb-5">
              <p className="text-white/40 text-sm">{messages.length === 0 ? "Zatiaľ žiadne správy." : `Správy (${messages.length})`}</p>
              <button type="button" onClick={openCompose} data-testid="admin-compose-email-btn"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-4 py-2.5 text-sm hover:bg-[#F2E3C6] transition-colors shrink-0">
                <Mail size={15} /> Napísať e-mail
              </button>
            </div>
            {msgToolbar()}
            {msgEmpty()}
            {messages.length === 0 ? (
            <p className="text-white/40 text-center py-16">Zatiaľ žiadne správy. E-mail môžete napísať cez tlačidlo „Napísať e-mail" vyššie.</p>
            ) : (
          <div className="space-y-4">
            {shownMessages.map((m) => (
              <div
                key={m.id}
                data-testid="admin-message-card"
                className={msgCardCls(m)}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    {msgHead(m)}
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-sm text-white/55">
                      <button type="button" onClick={() => { try { navigator.clipboard.writeText(m.email); } catch (e) {} }} title="Kliknutím skopírujete e-mail (neotvára osobný e-mail)" className="inline-flex items-center gap-1.5 hover:text-[#D4AF37] cursor-pointer bg-transparent border-0 p-0 text-inherit">
                        <Mail size={14} /> {m.email}
                      </button>
                      {m.company && (
                        <span className="inline-flex items-center gap-1.5"><Building2 size={14} /> {m.company}</span>
                      )}
                      <span className="inline-flex items-center gap-1.5 uppercase"><Globe size={14} /> {m.language}</span>
                      {m.consent && <span className="text-[#D4AF37]">✓ súhlas GDPR</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-white/35 text-xs">{fmtDate(m.created_at)}</span>
                    <button
                      onClick={() => remove(m.id)}
                      data-testid="admin-delete-message"
                      className="text-white/40 hover:text-red-400 transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 size={17} />
                    </button>
                  </div>
                </div>
                {msgText(m)}
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => openReply(m)}
                    data-testid="admin-reply-message"
                    className="inline-flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-4 py-2 text-sm hover:bg-[#D4AF37]/10 transition-colors"
                  >
                    <Reply size={15} /> Odpovedať
                  </button>
                  <button
                    onClick={() => toggleArchive(m.email)}
                    data-testid="admin-archive-toggle"
                    className="inline-flex items-center gap-2 border border-white/15 text-white/60 px-4 py-2 text-sm hover:border-[#D4AF37]/50 hover:text-white transition-colors"
                  >
                    <Inbox size={15} /> {archiveOpen[m.email] ? "Skryť archív" : "Archív odpovedí"}
                    {archive[m.email]?.length ? ` (${archive[m.email].length})` : ""}
                  </button>
                  {fwdBtn(m)}
                  {readBtn(m)}
                </div>

                {archiveOpen[m.email] && (
                  <div className="mt-4 border border-white/10 bg-[#050505] p-4" data-testid="admin-archive-list">
                    {archiveLoading === m.email && !archive[m.email] ? (
                      <p className="text-white/40 text-sm">Načítavam archív…</p>
                    ) : (archive[m.email]?.length ? (
                      <div className="space-y-2">
                        {archive[m.email].map((c, ci) => (
                          <div key={ci} className="flex items-start justify-between gap-3 border-b border-white/5 pb-2 last:border-0 last:pb-0">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.direction === "out" ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "bg-white/10 text-white/60"}`}>
                                  {c.direction === "out" ? "Odoslané" : "Prijaté"}
                                </span>
                                <span className="text-white/40 text-xs">{CORR_LABELS[c.ctype] || c.ctype}</span>
                                <span className="text-white/30 text-xs">· {fmtDate(c.created_at)}</span>
                              </div>
                              <p className="text-white/80 text-sm mt-1 truncate">{c.subject}</p>
                            </div>
                            {c.body_html && (
                              <button
                                onClick={() => viewCorrHtml(c.body_html)}
                                data-testid="admin-archive-view"
                                className="shrink-0 text-[#D4AF37] text-xs border border-[#D4AF37]/40 px-2.5 py-1 hover:bg-[#D4AF37]/10 transition-colors"
                              >
                                Zobraziť
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-white/40 text-sm">Zatiaľ žiadna korešpondencia s týmto kontaktom.</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
            )}
          </div>
        )}
        </div>
      </main>

      {corrView !== null && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/80 sm:px-4"
          data-testid="corr-view-modal"
          onClick={() => setCorrView(null)}
        >
          <div
            className="w-full sm:max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[85vh] bg-[#0a0a0a] sm:border border-white/15 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <span className="text-white/70 text-sm">Náhľad korešpondencie</span>
              <button onClick={() => setCorrView(null)} data-testid="corr-view-close" className="w-10 h-10 -mr-2 flex items-center justify-center text-white/60 hover:text-white text-lg">✕</button>
            </div>
            <iframe
              title="Korešpondencia"
              sandbox=""
              srcDoc={previewDoc(corrView)}
              data-testid="corr-view-frame"
              className="w-full flex-1 min-h-0 sm:flex-none sm:h-[70vh] bg-white"
              style={{ border: 0 }}
            />
          </div>
        </div>
      )}

      {reply && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          data-testid="reply-modal"
          onClick={() => !sending && setReply(null)}
        >
          <div
            className="w-full max-w-lg border border-white/15 bg-[#0a0a0a] p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-2xl mb-1">{reply.forward ? "Preposlať e-mail" : reply.compose ? "Napísať e-mail" : "Odpovedať"}</h3>
            {reply.compose ? (
              <div className="mb-5 mt-3">
                <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">Komu (e-mail príjemcu)</label>
                <input type="email" value={reply.email} onChange={(e) => setReply((r) => ({ ...r, email: e.target.value }))} data-testid="reply-to"
                  placeholder="email@prijemca.sk" className="w-full bg-[#050505] border border-white/15 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37]" />
              </div>
            ) : (
              <p className="text-white/45 text-sm mb-6 inline-flex items-center gap-2">
                <Mail size={14} /> {reply.email}
              </p>
            )}
            <div className="mb-5"><EmailLangPicker value={reply.lang || ""} onChange={(v) => setReply((r) => ({ ...r, lang: v }))} testid="reply-lang" /></div>
            <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">Predmet</label>
            <input
              value={reply.subject}
              onChange={(e) => setReply((r) => ({ ...r, subject: e.target.value }))}
              data-testid="reply-subject"
              className="w-full bg-[#050505] border border-white/15 px-3 py-2.5 text-white text-sm mb-5 focus:outline-none focus:border-[#D4AF37]"
            />
            <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">Správa</label>
            <textarea
              value={reply.body}
              onChange={(e) => setReply((r) => ({ ...r, body: e.target.value }))}
              data-testid="reply-body"
              rows={7}
              placeholder="Napíšte odpoveď…"
              className="w-full bg-[#050505] border border-white/15 px-3 py-2.5 text-white text-sm mb-4 focus:outline-none focus:border-[#D4AF37] resize-none"
            />

            <label className="block text-xs uppercase tracking-wider text-white/40 mb-2">Prílohy</label>
            <div className="flex flex-wrap gap-2 mb-3">
              <button
                type="button"
                onClick={() => attachTemplate("/cennik-saubereko.pdf", "cennik-saubereko.pdf")}
                data-testid="reply-attach-cennik"
                className="inline-flex items-center gap-1.5 border border-[#D4AF37]/50 text-[#D4AF37] px-3 py-1.5 text-xs hover:bg-[#D4AF37]/10 transition-colors"
              >
                <FileText size={13} /> Cenník (PDF)
              </button>
              <button
                type="button"
                onClick={() => { const sfx = ({ de: "-de", en: "-en", pl: "-pl", fr: "-fr" })[reply.lang] || ""; attachTemplate(`/co-je-saubereko${sfx}.pdf`, `co-je-saubereko${sfx}.pdf`); }}
                data-testid="reply-attach-brand"
                className="inline-flex items-center gap-1.5 border border-[#D4AF37]/50 text-[#D4AF37] px-3 py-1.5 text-xs hover:bg-[#D4AF37]/10 transition-colors"
              >
                <FileText size={13} /> Leták SauberEko® (PDF)
              </button>
              <button
                type="button"
                onClick={() => attachTemplate("/tig-trening-saubereko.pdf", "tig-trening-saubereko.pdf")}
                data-testid="reply-attach-tig"
                className="inline-flex items-center gap-1.5 border border-[#D4AF37]/50 text-[#D4AF37] px-3 py-1.5 text-xs hover:bg-[#D4AF37]/10 transition-colors"
              >
                <FileText size={13} /> TIG leták (PDF)
              </button>
              <label
                data-testid="reply-attach-file"
                className="inline-flex items-center gap-1.5 border border-white/20 text-white/70 px-3 py-1.5 text-xs hover:border-[#D4AF37]/50 hover:text-white cursor-pointer transition-colors"
              >
                <Paperclip size={13} /> Nahrať súbor
                <input type="file" multiple onChange={onPickFiles} className="hidden" />
              </label>
            </div>
            {reply.attachments?.length > 0 && (
              <div className="mb-6 space-y-2" data-testid="reply-attachments-list">
                {reply.attachments.map((a) => (
                  <div key={a.filename} className="flex items-center justify-between gap-3 bg-[#050505] border border-white/10 px-3 py-2 text-sm">
                    <span className="inline-flex items-center gap-2 text-white/75 truncate">
                      <Paperclip size={13} className="text-[#D4AF37] shrink-0" /> <span className="truncate">{a.filename}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.filename)}
                      data-testid={`reply-remove-${a.filename}`}
                      className="text-white/40 hover:text-red-400 transition-colors shrink-0"
                      aria-label="Odstrániť prílohu"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-3">
              <button
                onClick={() => setReply(null)}
                disabled={sending}
                className="px-5 py-2.5 text-sm text-white/60 hover:text-white transition-colors disabled:opacity-50"
              >
                Zrušiť
              </button>
              <button type="button" onClick={previewReply} disabled={previewing || sending} data-testid="reply-preview" className="inline-flex items-center gap-2 border border-[#D4AF37]/60 text-[#D4AF37] px-5 py-2.5 text-sm hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50"><Eye size={15} /> {previewing ? "Načítavam…" : "Náhľad"}</button>
              <button
                onClick={sendReply}
                disabled={sending || !reply.body.trim() || (reply.compose && !reply.email.trim())}
                data-testid="reply-send"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-6 py-2.5 text-sm hover:bg-[#F2E3C6] transition-colors disabled:opacity-50"
              >
                {sending ? "Odosielam…" : "Odoslať"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PushBell({ token }) {
  const [state, setState] = useState(() => (typeof Notification !== "undefined" ? Notification.permission : "default"));
  const [busy, setBusy] = useState(false);
  if (!pushSupported()) {
    const isIOS = typeof navigator !== "undefined" &&
      (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
       (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
    const standalone = typeof navigator !== "undefined" && navigator.standalone;
    if (isIOS && !standalone) {
      const howto = () => window.alert(
        "Notifikácie na iPhone/iPade:\n\n" +
        "1. Ťuknite na ikonu Zdieľať (štvorček so šípkou hore)\n" +
        "2. Zvoľte „Pridať na plochu\"\n" +
        "3. Otvorte appku z novej ikony na ploche\n" +
        "4. Tu v hlavičke sa objaví „Povoliť notifikácie\" — ťuknite a povoľte\n\n" +
        "(V prehliadači Safari Apple notifikácie nedovolí — iba z appky na ploche.)"
      );
      return (
        <button onClick={howto} data-testid="push-ios-hint"
          className="inline-flex items-center gap-1.5 border border-[#D4AF37]/30 text-[#D4AF37]/90 px-2.5 py-2 text-xs hover:bg-[#D4AF37]/10 transition-colors shrink-0">
          <Bell size={14} /> <span className="hidden sm:inline">Notifikácie: pridať na plochu</span>
        </button>
      );
    }
    return null;
  }
  const enable = async () => {
    setBusy(true);
    try {
      await enablePush(token);
      setState("granted");
      await sendPushTest(token);
    } catch (e) {
      if (String(e?.message) === "denied") setState("denied");
    } finally {
      setBusy(false);
    }
  };
  if (state === "granted") {
    return (
      <span data-testid="push-enabled" className="inline-flex items-center gap-1.5 text-[#D4AF37] text-xs border border-[#D4AF37]/30 px-2.5 py-2 shrink-0">
        <BellRing size={14} /> <span className="hidden sm:inline">Notifikácie zapnuté</span>
      </span>
    );
  }
  return (
    <button onClick={enable} disabled={busy} data-testid="push-enable"
      className="inline-flex items-center gap-1.5 border border-white/15 px-2.5 py-2 text-xs hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors shrink-0">
      <Bell size={14} /> <span className="hidden sm:inline">{busy ? "Zapínam…" : (state === "denied" ? "Notifikácie blokované" : "Povoliť notifikácie")}</span>
    </button>
  );
}

function NewBadge({ n, testid }) {
  if (!n || n <= 0) return null;
  return (
    <span
      data-testid={testid}
      className="ml-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
    >
      {n > 99 ? "99+" : n}
    </span>
  );
}
